import type { ConfigEntityDetail } from '../../types/entities';
import type { AuditLogDataDifference } from '../../types/audit-log';

/**
 * Recursively parses entity data into a flat array of path/value pairs.
 * This is a simplified version of the parseEntityData function from core-react
 * that we use here to avoid circular dependencies.
 *
 * @param data - The data object to parse
 * @param parentPath - The current path prefix
 * @returns Array of path/value pairs
 */
const parseEntityData = (
  data: any,
  parentPath = '',
): Array<{ path: string; data: unknown }> => {
  let result: Array<{ path: string; data: unknown }> = [];

  Object.keys(data ?? {}).forEach((key) => {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;

    if (typeof data[key] === 'object' && data[key] !== null) {
      result = result.concat(parseEntityData(data[key], currentPath));
    } else {
      result.push({ path: currentPath, data: data[key] });
    }
  });

  return result;
};

/**
 * Checks if a value is effectively empty (null, undefined, empty string, empty array, or empty object)
 * So we aren't showing changes to empty fields.
 */
const isEffectivelyEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 0
  ) {
    return true;
  }
  return false;
};

/**
 * Compares two entity data objects and returns differences.
 * Leverages the same pattern used by the existing view-all-data modal.
 *
 * @param fromData - The data from the older entity version
 * @param toData - The data from the newer entity version
 * @param pathPrefix - Optional prefix for the paths (e.g., 'domainEntityAttributes')
 * @returns Array of differences
 */
const compareEntityData = (
  fromData: any,
  toData: any,
  pathPrefix?: string,
): AuditLogDataDifference[] => {
  const fromParsed = parseEntityData(fromData, pathPrefix);
  const toParsed = parseEntityData(toData, pathPrefix);

  // Get all unique paths from both datasets
  const allPaths = Array.from(
    new Set([
      ...fromParsed.map(({ path }) => path),
      ...toParsed.map(({ path }) => path),
    ]),
  );

  const differences: AuditLogDataDifference[] = allPaths
    .filter((path) => {
      // Filter out domainEntityAttributes.entityMetadata paths
      if (path.startsWith('domainEntityAttributes.entityMetadata')) {
        return false;
      }

      // Filter out entityMetadata paths except for name
      if (
        path.startsWith('entityMetadata.') &&
        path !== 'entityMetadata.name'
      ) {
        return false;
      }

      return true;
    })
    .map((path) => {
      const fromItem = fromParsed.find((item) => item.path === path);
      const toItem = toParsed.find((item) => item.path === path);

      const fromValue = fromItem?.data;
      const toValue = toItem?.data;

      if (fromValue !== toValue) {
        // Skip if both values are effectively empty (e.g., null -> "", undefined -> []
        if (isEffectivelyEmpty(fromValue) && isEffectivelyEmpty(toValue)) {
          return null;
        }

        // Skip if one value is effectively empty and the other is also empty or false-y
        // This handles cases like null -> 0, undefined -> 0, "" -> 0, etc.
        // But preserve explicit false values
        if (
          (isEffectivelyEmpty(fromValue) && !toValue && toValue !== false) ||
          (isEffectivelyEmpty(toValue) && !fromValue && fromValue !== false)
        ) {
          return null;
        }

        return {
          affectedObject: path,
          changedFrom: fromValue,
          changedTo: toValue,
        } as AuditLogDataDifference;
      }
      return null;
    })
    .filter((diff): diff is AuditLogDataDifference => diff !== null);

  return differences;
};

/**
 * Calculates the differences between two ConfigEntityDetail objects.
 * If fromEntity is null, treats all data in toEntity as new (for entity creation).
 *
 * @param fromEntity - The older version of the entity (null for entity creation)
 * @param toEntity - The newer version of the entity
 * @returns Object containing the differences array and count of changes
 */
export const calculateEntityDiff = (
  fromEntity: ConfigEntityDetail | null,
  toEntity: ConfigEntityDetail,
): {
  differences: AuditLogDataDifference[];
  numberOfChanges: number;
} => {
  const allDifferences = compareEntityData(
    fromEntity?.attributes,
    toEntity.attributes,
  );

  return {
    differences: allDifferences,
    numberOfChanges: allDifferences.length,
  };
};
